import { File, FileType, Folder } from '../types'

/**
 * @desc 文件夹树管理器
 */
export class FolderTreeManager {
  /** 节点映射 (文件夹 / 文件 id => 对象) */
  private nodeMap: Map<string, Folder | File> = new Map()
  /** 路径映射 (文件夹 / 文件 id => id路径[])*/
  private pathMap: Map<string, string[]> = new Map()
  constructor(public rootFolders: Folder) {
    this.rebuildTree(rootFolders)
  }

  /** @description 基于新数据重新构建树 */
  rebuildTree(rootFolders: Folder) {
    this.nodeMap.clear()
    this.pathMap.clear()
    this.rootFolders = rootFolders
    this.buildEntityMap(rootFolders)
  }
  /** @description 判断文件夹中是否存在指定类型的文件 */
  hasFileOfType(id: string, type: FileType) {
    const folder = this.getEntityById(id) as Folder
    if (!folder) return false
    return folder.accept.supportedFiles.find((file) => file.type === type)
  }
  /** @description 获取整个树节点 */
  getTreeNode() {
    return this.rootFolders
  }
  /** @description 新建文件夹 */
  createFolder(folder: Folder) {
    // 如果是初次创建则直接添加即可
    if (!this.rootFolders) {
      this.rootFolders = folder
      return
    }
    this.createChild(folder.pid, folder, 'folder')
  }

  /** @description 新建文件 */
  createFile(file: File) {
    this.createChild(file.pid, file, 'file')
  }

  /** @description 移动文件夹 */
  moveFolder(folder: Folder, pid?: string) {
    const node = this.getEntityById(folder.id) as Folder
    if (!node) return
    const newFolder = { ...folder }
    if (pid) newFolder.pid = pid
    this.deleteChild(node.id, 'folder')
    this.createFolder(newFolder)
    // 重新建立子节点和后代文件的索引
    this.rebuildDescendantIndexes(newFolder.id)
  }
  /** @description 移动文件 */
  moveFile(file: File, pid?: string) {
    const node = this.getEntityById(file.id) as File
    if (!node) return
    const newFile = { ...file }
    if (pid) newFile.pid = pid
    this.deleteChild(node.id, 'file')
    this.createFile(newFile)
  }
  /** @description 更新文件夹节点 */
  updateFolderNode(id: string, folder: Folder) {
    const targetFolder = this.getEntityById(id) as Folder
    const isRootFolder = !folder.pid
    // 根目录不用去寻找上层文件夹，只需要更新部分数据即可
    if (isRootFolder) {
      const rootNode = this.getEntityById(id) as Folder
      Object.assign(rootNode, folder)
      return
    }
    // 除根目录以外的文件夹更新
    if (targetFolder) {
      const parentNode = this.getEntityById(folder.pid || '') as Folder
      if (!parentNode) return
      const index = parentNode.children.findIndex(
        (child) => child.id === folder.id
      )
      // 先删除再重新添加, 不会影响功能，但是可以触发 VUE 的更新
      this.unregisterEntity(id)
      parentNode.children.splice(index, 1)
      parentNode.children.splice(index, 0, folder)
      this.registerEntity(folder.id, folder)
    }
  }

  /** @description 删除文件夹 */
  deleteFolder(id: string) {
    this.deleteChild(id, 'folder')
  }

  /** @description 删除文件 */
  deleteFile(id: string) {
    this.deleteChild(id, 'file')
  }
  /** 添加子节点（文件夹或文件） */
  private createChild(
    pid: string,
    child: Folder | File,
    type: 'folder' | 'file'
  ) {
    const parent = this.getEntityById(pid) as Folder
    if (!parent) return false
    if (type === 'folder') {
      parent.children.push(child as Folder)
    } else {
      parent.accept.supportedFiles.push(child as File)
    }
    this.registerEntity(child.id, child)
    return true
  }

  /** 删除子节点 */
  private deleteChild(id: string, type: 'folder' | 'file') {
    const node = this.getEntityById(id)
    if (!node) return
    const parentNode = this.getEntityById(node.pid) as Folder
    if (!parentNode) return
    if (type === 'folder') {
      parentNode.children = parentNode.children.filter((v) => v.id !== id)
      // 同时需要卸载文件夹下方的所有子文件夹/文件
      this.unregisterChildFolderAndFile(id)
    } else {
      parentNode.accept.supportedFiles =
        parentNode.accept.supportedFiles.filter((v) => v.id !== id)
      this.unregisterEntity(id)
    }
  }

  /** @description 初始构建节点映射 */
  private buildEntityMap(folderTree: Folder | Folder[]) {
    const folderTreeArr = Array.isArray(folderTree) ? folderTree : [folderTree]
    folderTreeArr.forEach((folder) => {
      this.registerEntity(folder.id, folder)
      // 子文件夹
      if (folder.children?.length) {
        this.buildEntityMap(folder.children as Folder[])
      }
      // 子文件
      if (folder.accept.supportedFiles) {
        folder.accept.supportedFiles.forEach((file) => {
          this.registerEntity(file.id, file)
        })
      }
    })
  }
  /** @description 注册节点 */
  private registerEntity(id: string, data: Folder | File) {
    this.nodeMap.set(id, data)
    this.registerPath(id)
  }
  /** @description 注册路径 */
  private registerPath(id: string) {
    const node = this.getEntityById(id)
    if (!node) return
    const path = [id]
    let currentNode = node
    if (!currentNode) return
    // 向上查找路径
    while (currentNode.pid) {
      path.unshift(currentNode.pid)
      currentNode = this.getEntityById(currentNode.pid) as Folder
    }
    this.pathMap.set(id, path)
  }
  /** @description 注销节点 */
  private unregisterEntity(id: string) {
    this.nodeMap.delete(id)
    this.unregisterPath(id)
  }
  /** @description 注销子文件夹以及其子文件 */
  private unregisterChildFolderAndFile(id: string) {
    const node = this.getEntityById(id) as Folder
    if (!node) return
    this.unregisterEntity(id)
    node.children.forEach((child) => {
      this.unregisterChildFolderAndFile(child.id)
    })
    node.accept.supportedFiles.forEach((file) => {
      this.unregisterEntity(file.id)
    })
  }
  /** @description 注销路径 */
  private unregisterPath(id: string) {
    this.pathMap.delete(id)
  }

  /** @description 重新建立指定文件夹的所有后代节点索引 */
  private rebuildDescendantIndexes(folderId: string) {
    const folder = this.getEntityById(folderId) as Folder
    if (!folder) return
    // 递归重建子文件夹索引
    if (folder.children?.length) {
      folder.children.forEach((childFolder) => {
        this.registerEntity(childFolder.id, childFolder)
        this.rebuildDescendantIndexes(childFolder.id)
      })
    }
    // 重建子文件索引
    if (folder.accept.supportedFiles?.length) {
      folder.accept.supportedFiles.forEach((file) => {
        this.registerEntity(file.id, file)
      })
    }
  }
  /** @description 根据 ID 获取节点（文件夹或文件） */
  getEntityById(id: string) {
    return this.nodeMap.get(id)
  }
  /** id 是否存在 */
  has(id: string) {
    return this.nodeMap.has(id)
  }
  /** @description 根据 ID 获取路径 */
  getPathById(id: string) {
    return this.pathMap.get(id)
  }
  /** @description 获取所有文件 */
  getAllFiles() {
    return (Array.from(this.nodeMap.values()) as File[]).filter((v) => v.isFile)
  }
  /** @description 获取所有文件夹 */
  getAllFolders() {
    return (Array.from(this.nodeMap.values()) as Folder[]).filter(
      (v) => v.isFolder
    )
  }
  /** @description 获取所有添加进入的文件的文件类型 */
  getAllFileTypes() {
    const set = new Set<FileType>()
    this.getAllFiles().forEach((v) => {
      set.add(v.type)
    })
    return Array.from(set)
  }
  /**
   * 判断文件 id是否可以添加进入指定文件夹
   * @param id 文件id
   * @param targetFolderId 目标文件夹 ID
   */
  isAllowedInFolder(id: string, targetFolderId: string) {
    const node = this.getEntityById(id)
    const targetFolder = this.getEntityById(targetFolderId) as Folder
    // 不存在文件 ID 或者父级说明此文件未第一次添加进入
    if (!node || !targetFolder) return true
    // 如果文件已经在目标文件夹中不做处理
    if (node.pid === targetFolderId) return
    // 如果放入的路径中存在其本身，说明为其子目录
    const path = this.getPathById(targetFolderId) ?? []
    if (path.includes(id)) return
    if (this.isExistSameNameFile(node, targetFolderId)) return
    return true
  }
  /** 是否存在同名文件 */
  isExistSameNameFile(data: Folder | File, targetFolderId: string) {
    const targetFolder = this.getEntityById(targetFolderId) as Folder
    if (!targetFolder) return false
    const fileNames = targetFolder.accept.supportedFiles.map((v) => v.name)
    const folderNames = targetFolder.children.map((v) => v.name)
    const names = (data as File).isFile ? fileNames : folderNames
    return names.includes(data.name)
  }
  /** 是否是根目录 */
  isRoot(id: string) {
    const node = this.getEntityById(id)
    if (!node) return false
    return node.pid === ''
  }
}
